import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import NavBar from '../components/NavBar';
import { gethinhAPI, formatApiError } from '../lib/api';
import { Download, Search } from 'lucide-react';
import { Input } from '../components/ui/input';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function History() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    gethinhAPI.getHistory()
      .then(r => setItems(r.data))
      .catch(err => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = items.filter(i =>
    !q || i.item_name?.toLowerCase().includes(q.toLowerCase()) || i.source_link?.toLowerCase().includes(q.toLowerCase()) || i.provider?.toLowerCase().includes(q.toLowerCase())
  );

  const total = items.reduce((sum, i) => sum + (i.cost_tl || 0), 0);

  const reDownload = async (i) => {
    try {
      const res = await fetch(`${BACKEND}/api/downloads/${i.download_id}/file`, { credentials: 'include' });
      if (!res.ok) {
        const j = await res.json().catch(()=>({}));
        throw new Error(j.detail || `Hata ${res.status}`);
      }
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const fname = match?.[1] || `cihanstock_${i.item_code || i.download_id}`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <NavBar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold mb-1">İndirme Geçmişi</h1>
            <p className="text-muted-foreground text-sm">Toplam {items.length} indirme · <span className="text-emerald-400 font-semibold">{total.toFixed(2)} TL</span> harcandı</p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Ara..." className="pl-9 bg-white/5 border-white/10" data-testid="history-search-input" />
          </div>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground py-16">Yükleniyor...</div>
        ) : filtered.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center" data-testid="history-empty">
            <Download className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">Henüz indirme geçmişin yok.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(i => (
              <HistoryCard key={i.download_id} item={i} onDownload={() => reDownload(i)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HistoryCard({ item, onDownload }) {
  const [thumbBlob, setThumbBlob] = useState(null);
  const [thumbErr, setThumbErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/downloads/${item.download_id}/thumb`, { credentials: 'include' });
        if (!res.ok) throw new Error('thumb');
        const blob = await res.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setThumbBlob(url);
      } catch {
        if (!cancelled) setThumbErr(true);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [item.download_id]);

  return (
    <div className="glass-card rounded-2xl overflow-hidden flex flex-col" data-testid={`history-item-${item.download_id}`}>
      <div className="aspect-[4/3] bg-white/5 relative overflow-hidden">
        {thumbBlob ? (
          <img src={thumbBlob} alt={item.item_code} className="w-full h-full object-cover" />
        ) : thumbErr ? (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <Download className="w-8 h-8" />
          </div>
        ) : (
          <div className="w-full h-full animate-pulse bg-white/5" />
        )}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider bg-black/60 backdrop-blur text-white border border-white/10">
          {item.provider}
        </div>
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div className="min-w-0">
          <p className="font-medium text-sm truncate">cihanstock_{item.item_code}</p>
          <p className="text-xs text-muted-foreground">
            {item.item_type && <>{item.item_type} · </>}
            {item.item_size && <>{item.item_size} · </>}
            {new Date(item.downloaded_at).toLocaleString('tr-TR')}
          </p>
        </div>
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/5">
          <span className="text-xs font-semibold text-emerald-400 tabular-nums">-{item.cost_tl} TL</span>
          <button onClick={onDownload} className="text-xs text-fuchsia-400 hover:text-fuchsia-300 flex items-center gap-1" data-testid={`re-download-${item.download_id}`}>
            <Download className="w-3.5 h-3.5" /> Tekrar indir
          </button>
        </div>
      </div>
    </div>
  );
}
